import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { heroImageProps, heroContentProps } from '../../helpers';
import HeroGridWrapper from '../HeroGridWrapper';
import HeroContent from '../HeroContent';
import HeroImage from '../HeroImage';

export const generateHeroClassNames = ({ alignment }) => classNames(
  'grid-x',
  'hero',
  'hero-full-asymmetric',
  {
    'align-bottom': alignment === 'bottom',
  },
);

export const generateHeroImageClassNames = ({ alignment }) => classNames(
  'cell',
  'xsmall-16',
  'hero-full-asymmetric__image',
  {
    'hero-full-asymmetric__image--offset-top': alignment === 'top',
    'hero-full-asymmetric__image--offset-bottom': alignment === 'bottom',
  },
);

export const generateHeroContentClassNames = ({
  isContentFirst,
  alignment,
  backgroundType,
  theme,
}) => classNames(
  'cell',
  'xsmall-14',
  'xsmall-offset-1',
  'large-8',
  'xsmall-text--center',
  'hero-full-asymmetric__content',
  {
    'large-offset-1': isContentFirst,
    'large-offset-7': !isContentFirst,
    'hero-full-asymmetric__content--top': alignment === 'top',
    'hero-full-asymmetric__content--bottom': alignment === 'bottom',
    [`hero-full-asymmetric__content-${backgroundType}--${theme}`]: backgroundType && theme,
  },
);

const HeroFullAssymetric = ({
  heroData,
  isUnderlined = true,
  isContentFirst = false,
  fullWidth = true,
  alignment = 'top',
  ctaType = 'link',
  backgroundType = 'solid',
  theme = 'primary',
  headingLevel = 1,
  onClick,
  headingHasLink,
  imageHasLink,
  openLinkInNewTab = false,
}) => (
  <HeroGridWrapper
    fullWidth={fullWidth}
  >
    <div className={generateHeroClassNames({ alignment })}>
      <HeroContent {...heroContentProps({
          heroData,
          isUnderlined,
          ctaType,
          headingLevel,
          heroContentClassName: generateHeroContentClassNames({
            backgroundType,
            alignment,
            isContentFirst,
            theme,
          }),
          heroContentBoxClassName: 'hero-full-asymmetric__content-box',
          labelExtraClass: 'hero-full-asymmetric__label',
          headingExtraClass: 'hero-full-asymmetric__heading',
          headingIntroExtraClass: 'hero-full-asymmetric__heading-introduction',
          descriptionExtraClass: 'hero-full-asymmetric__text',
          ctaExtraClass: 'hero-full-asymmetric',
          onClick,
          headingHasLink,
          openLinkInNewTab,
        })}
      />
      <HeroImage {...heroImageProps({
          heroData,
          imageHasLink,
          heroImageClassName: generateHeroImageClassNames({ alignment }),
        })}
      />
    </div>
  </HeroGridWrapper>
);

HeroFullAssymetric.propTypes = {
  heroData: PropTypes.shape({
    description: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
    link: PropTypes.shape({
      linkText: PropTypes.string,
      linkUrl: PropTypes.string,
    }),
    imageSrc: PropTypes.string,
    imageSrcSet: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      src: PropTypes.string.isRequired,
    })),
    heroTag: PropTypes.string,
  }),
  isUnderlined: PropTypes.bool,
  alignment: PropTypes.oneOf(['top', 'bottom']),
  isContentFirst: PropTypes.bool,
  fullWidth: PropTypes.bool,
  backgroundType: PropTypes.oneOf(['solid', 'alpha']),
  ctaType: PropTypes.oneOf(['link', 'button']),
  theme: PropTypes.oneOf(['primary', 'secondary']),
  headingLevel: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  onClick: PropTypes.func,
  headingHasLink: PropTypes.bool,
  imageHasLink: PropTypes.bool,
  openLinkInNewTab: PropTypes.bool,
};

export default HeroFullAssymetric;
